import { integrationApi } from "@repo/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { getCronExpression } from "../../../utils/schedule";
import { getRegionsForApi } from "./utils";

interface UseUpdateIntegrationProps {
  integrationId: number;
  onSuccess: (
    message: string,
    isDraft?: boolean,
    integrationType?: string,
  ) => void;
  onError: (message: string) => void;
}

export function useUpdateIntegration({
  integrationId,
  onSuccess,
  onError,
}: UseUpdateIntegrationProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      isEnabled,
      data,
    }: {
      integrationType: string;
      isEnabled: boolean;
      data: Record<string, unknown>;
    }) => {
      // featureNames is always an array ([] before API loads, populated after), so || fallback
      // won't trigger for an empty array — submit is disabled until the form is valid, by which
      // time the API response is available and featureNames will be populated.
      const featureNames = (data.featureNames as string[]) || [
        "POSTURE_FOR_AWS",
      ];
      const features = featureNames.map((featureName) => ({
        featureName,
        featureConfig: {
          regions: getRegionsForApi((data.regions as string[]) || ["All"]),
        },
        schedules: [
          {
            scheduleName: "Daily",
            cronExpression: getCronExpression(
              (data.frequency as string) || "24hours",
              data.importTime as Date | null,
            ),
          },
        ],
      }));

      const payload = {
        integrationId,
        displayName: (data.name as string) || "",
        isEnabled,
        credentials: {
          type: "AWS_ROLE",
          data: {
            roleArn: (data.role_arn as string) || "",
            externalId: (data.external_id as string) || "",
          },
          metadata: {},
        },
        features,
        metadata: {
          accountId: (data.account_id as string) || "",
          description: data.description as string,
        },
      };
      return integrationApi.updateIntegration(integrationId, payload);
    },
    onError: () => {
      onError(
        intl.formatMessage({
          id: "M54/cu",
          defaultMessage: "Failed to update integration",
          description: "Error message for integration update failure",
        }),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      queryClient.invalidateQueries({
        queryKey: ["integration", integrationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationCredentials", integrationId],
      });
      const isDraft = !variables.isEnabled;
      if (isDraft) {
        onSuccess(
          intl.formatMessage(
            {
              id: "c2bz4O",
              defaultMessage:
                "You can resume the setup of {integrationType} at any time.",
              description: "Draft save success message",
            },
            { integrationType: variables.data.name as string },
          ),
          true,
          variables.data.name as string,
        );
      } else {
        onSuccess(
          intl.formatMessage({
            id: "1rvQ3c",
            defaultMessage: "Integration updated successfully",
            description: "Success message for integration update",
          }),
          false,
        );
      }
    },
  });
}
